import React, {ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {InlineNotification} from '@veneer/core';

import {PatchedTenantDetailRequest, ResponseError, TenantDetail, TenantPermission} from '@br/idm-api';
import {BuyOrActivateLicenseContext, GlobalTranslationsContext, Loading} from '@br/idm-components';

import {NotFound} from '../../components/NotFound';
import {useCustomerApi} from '../CustomerApi';
import {TenantUserProvider} from '../TenantUser/TenantUser';

import {TenantContextType} from './types';

export const TenantContext = React.createContext<TenantContextType>({} as TenantContextType);

const TenantContextProvider = ({children, tenantId}: {children: ReactNode; tenantId: string}): JSX.Element => {
    const {t} = useTranslation(['tenant']);

    const {tenantApi} = useCustomerApi();
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);

    const [tenant, setTenant] = useState<TenantDetail | null>(null);
    const [error, setError] = useState<ResponseError | null>(null);
    const [permissions, setPermissions] = useState<TenantPermission[]>([]);

    const hasPermission = useCallback(
        (permission: TenantPermission) => {
            return permissions.includes(permission);
        },
        [permissions]
    );

    const buyLicenseUrl = 'https://www.hpwolf.com/buy';
    const activateLicenseUrl = '/portal/activate/';
    const showBuyActivateLinks = useMemo(() => hasPermission(TenantPermission.ActivateLicense), [hasPermission]);

    useEffect(() => {
        Promise.all([tenantApi.tenantRetrieve({id: tenantId}), tenantApi.tenantUserCurrentRetrieve({tenantId})]).then(
            ([tenant, user]) => {
                if (tenant.controller?.loginUrl && !user.permissions.includes(TenantPermission.ViewIdmUi)) {
                    window.location.assign(tenant.controller.loginUrl);
                    return;
                }
                setTenant(tenant);
                setPermissions(user.permissions);
            },
            setError
        );
    }, [tenantId, tenantApi]);

    const updateTenant = useCallback(
        (tenantDetail: PatchedTenantDetailRequest) => {
            return tenantApi
                .tenantPartialUpdate({id: tenantId, patchedTenantDetailRequest: tenantDetail})
                .then(setTenant);
        },
        [tenantId, tenantApi]
    );

    if (error?.response) {
        if (error.response.status === 404) {
            return (
                <NotFound
                    message={t(
                        'tenant:tenantNotFound',
                        'A tenant with the ID \u2018{{tenantId}}\u2019 could not be found.',
                        {tenantId}
                    )}
                />
            );
        }
        return (
            <InlineNotification
                type="negative"
                title={t('tenant:tenantFetchFailed', 'Failed to fetch tenant.')}
                description={error.response.statusText}
                closeButton={false}
                hideIcon={false}
                i18n={veneerTranslations.inlineNotification}
            />
        );
    }

    if (tenant) {
        return (
            <TenantContext.Provider value={{tenant, updateTenant}}>
                <TenantUserProvider
                    tenantId={tenantId}
                    hasPermission={hasPermission}
                >
                    <BuyOrActivateLicenseContext.Provider
                        value={{
                            buyLicenseUrl,
                            activateLicenseUrl,
                            showBuyActivateLinks,
                        }}
                    >
                        {children}
                    </BuyOrActivateLicenseContext.Provider>
                </TenantUserProvider>
            </TenantContext.Provider>
        );
    }

    return <Loading />;
};

const useTenantContext = () => React.useContext(TenantContext);

export {TenantContextProvider, useTenantContext};
